import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/analytics';

interface Props {
  children: ReactNode;
  /** Receives the error and a callback that clears it and re-renders children. */
  fallback: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors, including promises rejected through `use()`.
 * React has no hook equivalent, so this stays a class component.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    // Sent only if the learner opted in; otherwise a no-op.
    reportError(error, { componentStack: info.componentStack ?? null });
    if (import.meta.env.DEV) console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private readonly reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    return error === null ? this.props.children : this.props.fallback(error, this.reset);
  }
}
